import React, { useCallback, useEffect, useState } from 'react';
import { FiCheckSquare, FiCheck, FiPlus, FiEdit2, FiRefreshCw } from 'react-icons/fi';
import { dutiesAPI } from '../services/api';
import './MarketingPlan.css';

// Each role's recurring duties, ticked every day / week / month (founder, 22 Oct 2026:
// "checkboxes are very important for every process"). A period that passes unticked is
// missed: it shows on the Action Board and counts against KPI V2 (deadlines met).
// The Chairman, CEO and COO edit every role's list; seeded duties are drafts until confirmed.

const STATE = {
  DONE: { label: 'Done', bg: '#ecfdf5', fg: '#047857' },
  DONE_LATE: { label: 'Done late', bg: '#fffbeb', fg: '#b45309' },
  DUE: { label: 'Due', bg: '#f1f5f9', fg: '#475569' },
  OVERDUE: { label: 'Overdue', bg: '#fef2f2', fg: '#b91c1c' },
  NOT_STARTED: { label: 'Starts next period', bg: '#f8fafc', fg: '#94a3b8' },
};
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const FREQS = [{ value: 'DAILY', label: 'Every working day' }, { value: 'WEEKLY', label: 'Every week' }, { value: 'MONTHLY', label: 'Every month' }];
const fmt = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '');

const PeriodLine = ({ duty, p, canTick, onChanged, late }) => {
  const st = STATE[p.state];
  const [busy, setBusy] = useState(false);
  const done = p.state === 'DONE' || p.state === 'DONE_LATE';
  const toggle = async () => {
    setBusy(true);
    try {
      if (done) await dutiesAPI.untick(duty.id, p.period_start); else await dutiesAPI.tick(duty.id, p.period_start);
      onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not save.');
    } finally { setBusy(false); }
  };
  return (
    <div className="mp-row" style={{ fontSize: 13, marginTop: 4 }}>
      {canTick ? (
        <input type="checkbox" checked={done} disabled={busy} onChange={toggle} />
      ) : <span style={{ width: 13 }} />}
      <span>{late ? 'Missed: ' : ''}{p.period_label} · due {fmt(p.due_date)}</span>
      <span className="mp-status" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
      {p.done_by && <span className="mp-muted">by {p.done_by}</span>}
    </div>
  );
};

const DutyForm = ({ initial, roles, onDone }) => {
  const [f, setF] = useState({ role: 'CMO', title: '', description: '', frequency: 'WEEKLY', due_weekday: 4, due_day: 25,
    kpi_codes: '', is_draft: false, ...(initial || {}) });
  const [err, setErr] = useState('');
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      if (initial?.id) await dutiesAPI.update(initial.id, f); else await dutiesAPI.create(f);
      onDone();
    } catch (e) {
      const d = e?.response?.data;
      setErr(d?.detail || (d && Object.values(d)[0]?.[0]) || 'Could not save.');
    }
  };
  return (
    <div className="mp-form">
      <div className="mp-row">
        <select value={f.role} onChange={(e) => set('role', e.target.value)}>
          {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <input className="mp-grow" placeholder="Duty, e.g. Answer every new lead within one working day" value={f.title} onChange={(e) => set('title', e.target.value)} />
      </div>
      <div className="mp-row">
        <select value={f.frequency} onChange={(e) => set('frequency', e.target.value)}>
          {FREQS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {f.frequency === 'WEEKLY' && (
          <select value={f.due_weekday} onChange={(e) => set('due_weekday', Number(e.target.value))}>
            {WEEKDAYS.map((d, i) => <option key={d} value={i}>due {d}</option>)}
          </select>
        )}
        {f.frequency === 'MONTHLY' && (
          <label>due on day <input type="number" min="1" max="28" value={f.due_day} onChange={(e) => set('due_day', Number(e.target.value))} style={{ width: 60 }} /></label>
        )}
        <input placeholder="KPIs, e.g. CMO4" value={f.kpi_codes} onChange={(e) => set('kpi_codes', e.target.value)} style={{ width: 130 }} />
        <label><input type="checkbox" checked={!f.is_draft} onChange={(e) => set('is_draft', !e.target.checked)} /> Confirmed (counts for KPI V2)</label>
      </div>
      <textarea rows={2} placeholder="How to do it (optional)" value={f.description} onChange={(e) => set('description', e.target.value)} />
      {err && <div className="mp-error">{err}</div>}
      <div className="mp-row">
        <button className="mp-btn primary" onClick={save}><FiCheck /> Save</button>
        <button className="mp-btn" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
};

const MyDuties = () => {
  const [mine, setMine] = useState(null);
  const [all, setAll] = useState(null);
  const [role, setRole] = useState('');
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const [{ data: m }, { data: a }] = await Promise.all([
        dutiesAPI.mine(), dutiesAPI.list(role ? { role } : {}),
      ]);
      setMine(m);
      setAll(a);
    } catch {
      setError('Could not load duties.');
    }
  }, [role]);
  useEffect(() => { load(); }, [load]);

  const renderDuty = (d, canTick, manage) => (
    <div key={d.id} className="mp-item">
      {editing === d.id ? (
        <DutyForm initial={d} roles={all.roles} onDone={() => { setEditing(null); load(); }} />
      ) : (
        <div className="mp-item-top">
          <div className="mp-item-main">
            <div className="mp-item-title">
              {d.title}
              <span className="mp-tag">{d.frequency_label}</span>
              {d.is_draft && <span className="mp-tag" title="Seeded suggestion — not counted for KPI V2 until confirmed">Draft</span>}
              {d.kpi_codes && <span className="mp-tag">{d.kpi_codes}</span>}
              {manage && <span className="mp-muted">{all.roles.find((r) => r.value === d.role)?.label || d.role}</span>}
            </div>
            {d.description && <div className="mp-item-desc">{d.description}</div>}
            {d.missed && <PeriodLine duty={d} p={d.missed} canTick={canTick} onChanged={load} late />}
            <PeriodLine duty={d} p={d.current} canTick={canTick} onChanged={load} />
          </div>
          {manage && (
            <div className="mp-item-actions">
              <button className="mp-icon" title="Edit" onClick={() => setEditing(d.id)}><FiEdit2 /></button>
              <button className="mp-btn small" onClick={async () => { if (window.confirm(`Retire "${d.title}"? Its history stays.`)) { await dutiesAPI.retire(d.id); load(); } }}>Retire</button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="mp-page">
      <div className="mp-header">
        <div>
          <h1><FiCheckSquare /> My Duties</h1>
          <p className="mp-muted">Your role's routine, ticked each day, week or month. A missed tick shows on the Action Board.</p>
        </div>
        <button className="mp-icon" onClick={load} title="Refresh"><FiRefreshCw /></button>
      </div>
      {error && <div className="mp-error">{error}</div>}
      {mine && (
        <div className="mp-card">
          <h3>{mine.role_label}</h3>
          {mine.duties.length === 0 && <div className="mp-muted">No duties set for your role yet.</div>}
          {mine.duties.map((d) => renderDuty(d, true, false))}
        </div>
      )}
      {all?.can_manage && (
        <div className="mp-card">
          <div className="mp-week-head">
            <h3>Every role's duties</h3>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="">All roles</option>
              {all.roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <button className="mp-btn small" onClick={() => setEditing('new')}><FiPlus /> Add duty</button>
          </div>
          {editing === 'new' && <DutyForm roles={all.roles} initial={role ? { role } : null} onDone={() => { setEditing(null); load(); }} />}
          {all.duties.map((d) => renderDuty(d, true, true))}
        </div>
      )}
    </div>
  );
};

export default MyDuties;
